// The views of the agents space, as pure lists (no React, no DOM): which chips a
// place offers, in which order, with which count. The home (/agents) draws ONE chip
// row under the navigator's stats line (the activity space's pattern). An agent's own
// spaces are not chips: they are the navigator's words (lib/os/tree agentPlaceNodes).

export type HomeView = "orbit" | "agents" | "activity" | "identities";

/** the carrier a pick from outside leaves for a space that has not mounted yet (netti's UI bridge writes `innernet:panel-view:<panel>`) */
export const AGENTS_PANEL_VIEW_KEY = "innernet:panel-view:agents";

/** every view the home can have, the identities last; the chip of it stands only for whoever F.agentIdentity lets in */
export const HOME_VIEW_IDS: readonly HomeView[] = ["orbit", "agents", "activity", "identities"];

/** a view named from outside (the address's ?v=, a pick), or null when it is none of ours */
export function homeViewOf(v: unknown): HomeView | null {
  return typeof v === "string" && (HOME_VIEW_IDS as readonly string[]).includes(v) ? (v as HomeView) : null;
}

/**
 * The view a pick lands on, or null to ignore it: "identities" only where the space has it (agent identity,
 * F.agentIdentity), so a link to it for anyone else lands on the first view and never on an empty room.
 */
export function takeHomeView(v: unknown, identities: boolean): HomeView | null {
  const id = homeViewOf(v);
  return id === "identities" && !identities ? null : id;
}

/**
 * The view the server's address names (`/dashboard/agents?v=identities`): only for the agents home itself,
 * so a `?v=` meant for another place never reaches here, and an agent's own page (`/agents/<slug>`) is none.
 */
export function arrivalHomeView(place: string | null | undefined, v: string | null | undefined): HomeView | null {
  return place === "/agents" ? homeViewOf(v) : null;
}

export interface ChipSpec<V extends string = string> {
  id: V;
  label: string;
  /** a count that rides beside the word; null says no number (a zero is no figure at all) */
  n: number | null;
}

/**
 * The home's chips: orbit (the picture, and the way in), agents (the list, under one lens: the tools you
 * talk to are in it as the tools lens), activity (everything coming in from every agent, what waits
 * for your yes among it) and, for whoever can see agent identity (F.agentIdentity), identities: how you want
 * every ai to talk, answer and behave, and what netti noticed you asked of one (components/dashboard/
 * IdentitySettings; it was the netti space's view until 25 oct). The count beside agents is how many there
 * are (sub-agents too); the count beside activity is what waits for you. A zero is no figure at all, and the
 * identities carry none (what waits for you inside it is said inside it).
 */
export function homeViewsOf(a: { agents: number; waiting: number }, o: { identities?: boolean } = {}): ChipSpec<HomeView>[] {
  return [
    { id: "orbit", label: "orbit", n: null },
    { id: "agents", label: "agents", n: a.agents || null },
    { id: "activity", label: "activity", n: a.waiting || null },
    ...(o.identities ? [{ id: "identities" as const, label: "identities", n: null }] : []),
  ];
}

/** the chip a keyboard arrow lands on: ← → wrap round the row, Home and End go to its ends; null for any other key */
export function chipAfterKey<V extends string>(views: ReadonlyArray<{ id: V }>, active: V, key: string): V | null {
  if (views.length === 0) return null;
  const i = views.findIndex((v) => v.id === active);
  let to = -1;
  if (key === "ArrowRight") to = (i + 1) % views.length;
  else if (key === "ArrowLeft") to = (i - 1 + views.length) % views.length;
  else if (key === "Home") to = 0;
  else if (key === "End") to = views.length - 1;
  return to < 0 ? null : views[to].id;
}

// ── the views inside an agent's spaces (John, 2026-10-19: "more views inside the spaces to bifurcate the
// different data") ───────────────────────────────────────────────────────────────────────────────────────
// memory is the agent's own "you" space: what it holds at a glance, then each kind of holding on its own.
// activity is its river first (everything unfolding, what waits for you inside it), then its figures and runs.

export type MemoryView = "overview" | "keeps" | "projects" | "family";
export type ActivityView = "river" | "figures" | "rituals";

/** memory's chips: overview · what it keeps (n) · projects (n) · sub-agents (n), the last for a top-level agent only */
export function memoryViewsOf(a: { memories: number; projects: number; subAgents: number; top: boolean }): ChipSpec<MemoryView>[] {
  return [
    { id: "overview", label: "overview", n: null },
    { id: "keeps", label: "what it keeps", n: a.memories || null },
    { id: "projects", label: "projects", n: a.projects || null },
    ...(a.top ? [{ id: "family" as const, label: "sub-agents", n: a.subAgents || null }] : []),
  ];
}

/** activity's chips: river (how many wait for you) · figures · rituals */
export function activityViewsOf(a: { waiting: number }): ChipSpec<ActivityView>[] {
  return [
    { id: "river", label: "river", n: a.waiting || null },
    { id: "figures", label: "figures", n: null },
    { id: "rituals", label: "rituals", n: null },
  ];
}
